import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import {
  useExpenseSubCategories,
  createExpenseSubCategory,
  deleteExpenseSubCategory,
  ExpenseSubCategory,
} from './useData';

interface SubcategoriesSectionProps {
  categoryId: number;
}

const SubcategoriesSection: React.FC<SubcategoriesSectionProps> = ({ categoryId }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { data: subcategories, isLoading, mutate } = useExpenseSubCategories();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const list = (subcategories || []).filter(
    (s) => (s.exp_category_id ?? 0) === categoryId
  );

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      showError('Please enter a subcategory name.');
      return;
    }
    setSubmitting(true);
    try {
      await createExpenseSubCategory({ name: trimmed, exp_category_id: categoryId });
      showSuccess('Subcategory added.');
      setName('');
      mutate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(msg || 'Failed to add subcategory.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this subcategory?')) return;
    setDeletingId(id);
    try {
      await deleteExpenseSubCategory(id);
      showSuccess('Subcategory deleted.');
      mutate();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(msg || 'Failed to delete subcategory.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('text-sm', colors.textMuted)}>Loading subcategories...</div>
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label htmlFor="subcategory-name" className={cn('block text-xs font-medium mb-1', colors.textMuted)}>
            Add subcategory
          </label>
          <input
            id="subcategory-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Electricity, Water"
            className={cn(
              'w-full px-3 py-2 rounded-lg border text-sm',
              colors.surface,
              colors.border,
              colors.text,
              'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
            )}
            maxLength={255}
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Adding...' : 'Add'}
        </button>
      </form>

      {list.length === 0 ? (
        <p className={cn('text-sm italic', colors.textMuted)}>No subcategories yet.</p>
      ) : (
        <ul className={cn('space-y-1 border-l-2 pl-3', colors.border)}>
          {list.map((sub: ExpenseSubCategory) => (
            <li
              key={sub.id}
              className={cn(
                'flex items-center justify-between py-1.5 pr-2 rounded group',
                'hover:bg-gray-50 dark:hover:bg-gray-800/50'
              )}
            >
              <span className={cn('text-sm', colors.text)}>{sub.name || '-'}</span>
              <button
                type="button"
                onClick={() => handleDelete(sub.id)}
                disabled={deletingId === sub.id}
                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SubcategoriesSection;
